import { Typography } from '@material-ui/core';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  AnnotationDefinition,
  AnnotationDefinitionFilter,
  AnnotationDefinitionFilterElements,
  AnnotationDefinitionTableItem,
  AnnotationType,
  AnnotationsManagerSortBy,
} from '../../../models/annotation.types';
import { getAnnotationDefinitions } from '../../../services/admin/settings/annotations.service';
import { SelectableTable } from '../../SelectableTable/SelectableTable.component';
import { IndexedStrings, SelectableTableColumn } from '../../SelectableTable/SelectableTable.types';
import { useStyles } from '../SystemSettings.styles';
import { getAnnotationStatusLabel, getAnnotationTypeLabel } from './utils';
import DotWithLabel from '../../DotWithLabel.component';
import AnnotationPreview from '../../AnnotationPreview/AnnotationPreview.component';
import { AddAnnotationDefinition } from './AddAnnotationDefinition.component';
import { ItemsFilter } from '../../SelectableTable/filters/ItemsFilter.component';
import { ActionMenuItem } from '../../ActionsMenu/ActionsMenu.types';
import usePermission, { UserPermission } from '../../../hooks/usePermission';
import ActionsMenu from '../../ActionsMenu/ActionsMenu.component';
import { AnnotationDefinitionDetails } from './AnnotationDefinitionDetails.component';
import _ from 'lodash';
import { ChangeAnnotationDefinitionStatus } from './ChangeAnnotationDefinitionStatus.component';
import { useLayoutContext } from '../../../Context/Layout.context';

interface AnnotationDefinitionsManagerProps {
  onClose: () => void;
  onChange: () => void;
}

const defaultFilter = {
  annotationType: [],
  repetitive: [],
  name: '',
  description: '',
  active: [true],
};

const AnnotationDefinitionsManager: React.FC<AnnotationDefinitionsManagerProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const { openAlert } = useLayoutContext();

  const [annotations, setAnnotations] = useState<AnnotationDefinitionTableItem[]>([]);
  const [filter, setFilter] = useState<AnnotationDefinitionFilter>(defaultFilter);
  const [isValidFilter, setIsValidFilter] = useState<boolean>(true);
  const [sortByColumnId, setSortByColumnId] = useState<AnnotationsManagerSortBy>('annotationType');
  const [isDescending, setIsDescending] = useState<boolean>(true);

  const [editDetailsModalOpen, setEditDetailsModalOpen] = useState<boolean>(false);
  const [changeStatusOpen, setChangeStatusOpen] = useState<boolean>(false);
  const [selectedAnnotationDefinition, setSelectedAnnotationDefinition] = useState<AnnotationDefinition>();

  const hasAdminAnnotationManagePermission = usePermission(UserPermission.AdminAnnotationManage);

  const columns: SelectableTableColumn[] = [
    {
      id: 'icon',
      type: 'text',
      title: t('systemSettings:annotationDefinitionManager.table.icon'),
      filterType: null,
    },
    {
      id: 'annotationType',
      type: 'text',
      title: t('systemSettings:annotationDefinitionManager.table.annotationType'),
      filterType: 'filter',
      sortable: true,
    },
    {
      id: 'repetitive',
      type: 'text',
      title: t('systemSettings:annotationDefinitionManager.table.repetitive'),
      filterType: 'filter',
      sortable: true,
    },
    {
      id: 'name',
      type: 'text',
      title: t('systemSettings:annotationDefinitionManager.table.name'),
      filterType: 'search',
      sortable: true,
    },
    {
      id: 'description',
      type: 'text',
      title: t('systemSettings:annotationDefinitionManager.table.description'),
      filterType: 'search',
      sortable: true,
    },
    {
      id: 'active',
      type: 'text',
      title: t('systemSettings:annotationDefinitionManager.table.active'),
      filterType: 'filter',
      sortable: true,
    },
    {
      id: 'actions',
      type: 'text',
      title: 'Akcje',
      filterType: null,
      skipEnterMethod: true,
    },
  ];

  const handleFilterChange = useCallback((name: string, value: unknown) => {
    typeof value === 'string' && value.length === 1 ? setIsValidFilter(false) : setIsValidFilter(true);
    const newFilterAttribute: Partial<AnnotationDefinitionFilter> = { [name]: Array.isArray(value) ? value : [value] };
    setFilter((prevState) => {
      return {
        ...prevState,
        ...newFilterAttribute,
      };
    });
  }, [setFilter]);

  const filterElements: AnnotationDefinitionFilterElements = {
    annotationType: (
      <ItemsFilter<AnnotationType>
        valueName="annotationType"
        valueLabel="Typ adnotacji"
        onChange={handleFilterChange}
        resetFilter={!filter.annotationType.length}
        itemsWithValues={[
          { label: 'Dynamiczna', value: AnnotationType.dynamic },
          { label: 'Statyczna', value: AnnotationType.static },
        ]}
        filter={filter.annotationType}
      />
    ),
    repetitive: (
      <ItemsFilter<boolean>
        valueName="repetitive"
        valueLabel="Powtarzalność"
        onChange={handleFilterChange}
        resetFilter={!filter.repetitive.length}
        itemsWithValues={[
          { label: 'Tak', value: true },
          { label: 'Nie', value: false },
        ]}
        filter={filter.repetitive}
      />
    ),
    active: (
      <ItemsFilter<boolean>
        valueName="active"
        valueLabel="Status"
        onChange={handleFilterChange}
        resetFilter={!filter.active.length}
        filterState={filter.active}
        itemsWithValues={[
          { label: 'Aktywna', value: true },
          { label: 'Nieaktywna', value: false },
        ]}
        filter={filter.active}
      />
    ),
  };

  const uncheckAllFilters = () => setFilter(defaultFilter);

  const getActionMenuItems = (annotationDefinition: AnnotationDefinition): JSX.Element => {
    const menuItems: ActionMenuItem[] = [];
    if (hasAdminAnnotationManagePermission) {
      menuItems.push({
        text: 'Edytuj adnotację',
        disabled: !annotationDefinition.active,
        onClick: () => {
          setEditDetailsModalOpen(true);
        },
      });
    }
    menuItems.push({
      text: `${annotationDefinition.active ? 'Dezaktywuj' : 'Aktywuj'} adnotację`,
      onClick: () => {
        setChangeStatusOpen(true);
      },
    });
    return <ActionsMenu onOpen={() => setSelectedAnnotationDefinition(annotationDefinition)} menuItems={menuItems} />;
  };

  const refreshData = _.debounce((filter: AnnotationDefinitionFilter, signal?: AbortSignal) => {
    if (!isValidFilter) return;
    getAnnotationDefinitions(filter, sortByColumnId, isDescending, signal).then(
      (annotationDefinitions: AnnotationDefinition[]) => {
        const tableItems: AnnotationDefinitionTableItem[] = annotationDefinitions.map(
          (annotationDefinition: AnnotationDefinition) => {
            return {
              icon: (
                <div style={{ margin: '5px 0 5px 0' }}>
                  <AnnotationPreview svg={annotationDefinition.svg} />
                </div>
              ),
              annotationType: getAnnotationTypeLabel(annotationDefinition.annotationType),
              repetitive: (
                <DotWithLabel
                  color={annotationDefinition.repetitive ? '#347F42' : '#BC0000'}
                  label={annotationDefinition.repetitive ? 'Tak' : 'Nie'}
                />
              ),
              name: annotationDefinition.name,
              description: annotationDefinition.description,
              active: (
                <DotWithLabel
                  color={annotationDefinition.active ? '#347F42' : '#BC0000'}
                  label={getAnnotationStatusLabel(annotationDefinition.active)}
                />
              ),
              actions: getActionMenuItems(annotationDefinition),
            };
          },
        );
        setAnnotations(tableItems);
      },
      () => openAlert('get_error'),
    );
  }, 1000);

  useEffect(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    refreshData(filter, signal);
  }, [filter, isDescending, sortByColumnId]);

  const filterTranslated: IndexedStrings = useMemo(() => {
    const result: IndexedStrings = {};
    Object.keys(filter).forEach((key: string) => {
      result[key] = filter[key as keyof AnnotationDefinitionFilter]?.toString() ?? '';
    });
    return result;
  }, [filter]);

  const filterElementsTranslated: { [key: string]: JSX.Element } = {};
  Object.keys(filterElements).forEach((key: string) => {
    filterElementsTranslated[key] = filterElements[key as keyof AnnotationDefinitionFilterElements];
  });

  return (
    <>
      <div className={classes.listHeader}>
        <Typography className={classes.tabTitle}>
          {t('systemSettings:systemModalTitles.annotationDefinitionManager')}
        </Typography>
        <AddAnnotationDefinition onChange={() => refreshData(filter)} />
      </div>
      <div style={{ width: '100%', height: '450px', overflowY: 'auto' }}>
        <SelectableTable<AnnotationDefinitionTableItem, AnnotationsManagerSortBy>
          columns={columns}
          rows={annotations}
          filter={filterTranslated}
          filterElements={filterElementsTranslated}
          onFilterChange={handleFilterChange}
          uncheckAllFilters={uncheckAllFilters}
          sortByProperty={sortByColumnId}
          isDescending={isDescending}
          setIsDescending={setIsDescending}
          setSortByColumn={setSortByColumnId}
          caption={t('systemSettings:systemModalTitles.annotationDefinitionManager')}
        />
      </div>
      {selectedAnnotationDefinition !== undefined && (
        <>
          {editDetailsModalOpen && (
            <AnnotationDefinitionDetails
              title={`Edytuj adnotację: ${selectedAnnotationDefinition.name}`}
              open={editDetailsModalOpen}
              annotationDefinition={selectedAnnotationDefinition}
              onClose={() => {
                setEditDetailsModalOpen(false);
              }}
              onSave={() => {
                refreshData(filter);
                setEditDetailsModalOpen(false);
              }}
            />
          )}
          {changeStatusOpen && (
            <ChangeAnnotationDefinitionStatus
              annotationDefinition={selectedAnnotationDefinition}
              onSave={() => {
                refreshData(filter);
                setChangeStatusOpen(false);
              }}
              onCancel={() => setChangeStatusOpen(false)}
            />
          )}
        </>
      )}
    </>
  );
};

export default AnnotationDefinitionsManager;
